import { type FC, type ReactNode, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tabs — inline tabbed navigation used on hub pages (Access Manager).
 *
 * Does not deep-link by itself; the consumer wires active-tab state to
 * the URL if persistence is desired.
 */

export interface TabItem {
  key: string;
  label: string;
  badge?: ReactNode; // e.g. a count
}

export interface TabsProps {
  items: TabItem[];
  active: string;
  onChange: (key: string) => void;
  className?: string;
}

export const Tabs: FC<TabsProps> = ({ items, active, onChange, className }) => {
  // Arrow-key roving: Left/Right move focus + selection to the adjacent tab
  // (wrapping), per the WAI-ARIA tablist pattern.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = (index + delta + items.length) % items.length;
    const nextItem = items[next];
    if (!nextItem) return;
    onChange(nextItem.key);
    const buttons = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[next]?.focus();
  };

  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex items-center gap-1 p-1 liquid-glass rounded-glass-sm',
        className,
      )}
    >
      {items.map((item, index) => {
        const isActive = item.key === active;
        return (
          <button
            key={item.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, index)}
            onClick={() => onChange(item.key)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-caption font-medium transition-colors min-h-9',
              isActive
                ? 'bg-white text-brand-blue shadow-glass-sm'
                : 'text-text-secondary hover:bg-slate-900/5',
            )}
          >
            {item.label}
            {item.badge !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-micro font-semibold',
                  isActive ? 'bg-brand-blue/10 text-brand-blue' : 'bg-slate-900/8 text-text-secondary',
                )}
              >
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
